import { buildStatusPayload } from './statusWorkflowAdapter';
import { normalizeFieldOptions } from '../utils/fieldOptionsUtils';
import { isStatusField, isSubStatusField } from '../utils/formStudioFormUtils';
import {
  getSubstatusOptionsForParentOption,
  getUnionSubstatusOptions,
  hydrateSubStatusFieldFromApi,
} from '../utils/statusOptionSubstatusUtils';

const DEFAULT_STATUS_COLOR = '#4962e5';

export function getStatusId(status) {
  return status?.status_id ?? status?.id ?? status?.statusId ?? null;
}

export function buildStatusRecordsFromField(field) {
  const options = normalizeFieldOptions(field?.key_options);
  const colors = field?.option_colors || {};

  return options.map((statusName, index) => ({
    status_name: statusName,
    type_of_status: field.key_name,
    sort_num: index + 1,
    active: true,
    color: colors[statusName] || DEFAULT_STATUS_COLOR,
    substatuses: [],
    triggers: [],
  }));
}

export function mergeExistingStatusRecord(existing, desired) {
  return {
    ...existing,
    ...desired,
    status_id: getStatusId(existing),
    substatuses: Array.isArray(existing?.substatuses) ? existing.substatuses : [],
    triggers: Array.isArray(existing?.triggers) ? existing.triggers : [],
  };
}

export function diffStatusRecords(existing = [], desired = []) {
  const existingByName = new Map(
    existing.map((item) => [String(item?.status_name || '').trim().toLowerCase(), item])
  );
  const desiredNames = new Set(
    desired.map((item) => String(item?.status_name || '').trim().toLowerCase())
  );

  const toUpsert = desired.map((record) => {
    const match = existingByName.get(String(record.status_name || '').trim().toLowerCase());
    return match ? mergeExistingStatusRecord(match, record) : record;
  });

  const toDelete = existing.filter(
    (item) => !desiredNames.has(String(item?.status_name || '').trim().toLowerCase())
  );

  return { toUpsert, toDelete };
}

export function buildSubstatusEntries(subField, existingSubs = [], timestamp = Date.now()) {
  const options = normalizeFieldOptions(subField?.key_options);

  const existingByName = new Map(
    (existingSubs || []).map((sub) => [String(sub?.name || '').trim().toLowerCase(), sub])
  );

  return options.map((name, index) => {
    const existing = existingByName.get(String(name).trim().toLowerCase());
    return {
      name,
      type_of_status: subField.key_name,
      sub_status_id:
        existing?.sub_status_id ||
        `${subField.key_name}_${timestamp}_${index}`,
      triggers: existing?.triggers || [],
    };
  });
}

export function applySubstatusesToParentStatus(parentStatus, subField, timestamp = Date.now()) {
  const subFieldKey = subField.key_name;
  const preserved = (parentStatus?.substatuses || []).filter(
    (sub) => sub?.type_of_status !== subFieldKey
  );
  const existingForField = (parentStatus?.substatuses || []).filter(
    (sub) => sub?.type_of_status === subFieldKey
  );

  const optionNames = getSubstatusOptionsForParentOption(subField, parentStatus?.status_name);

  return {
    ...parentStatus,
    substatuses: [
      ...preserved,
      ...buildSubstatusEntries(
        { ...subField, key_options: optionNames },
        existingForField,
        timestamp
      ),
    ],
  };
}

export function getSyncableFields(fields = []) {
  return fields.filter((field) => isStatusField(field) || isSubStatusField(field));
}

export function statusFieldHasOptions(field) {
  if (isSubStatusField(field)) {
    return getUnionSubstatusOptions(field).length > 0;
  }
  return normalizeFieldOptions(field?.key_options).length > 0;
}

export function mergeStatusOptionsFromApi(statusField, apiStatuses = []) {
  const typeKey = statusField?.key_name;
  if (!typeKey) return statusField;

  const apiOptions = apiStatuses
    .filter((item) => item?.type_of_status === typeKey)
    .sort((left, right) => (left?.sort_num ?? 0) - (right?.sort_num ?? 0))
    .map((item) => item?.status_name)
    .filter(Boolean);

  if (!apiOptions.length) {
    return statusField;
  }

  const localOptions = normalizeFieldOptions(statusField.key_options);
  if (localOptions.length > 0) {
    return statusField;
  }

  const colors = { ...(statusField.option_colors || {}) };
  apiStatuses
    .filter((item) => item?.type_of_status === typeKey)
    .forEach((item) => {
      if (item?.status_name && item?.color) {
        colors[item.status_name] = item.color;
      }
    });

  return {
    ...statusField,
    key_options: apiOptions,
    option_colors: colors,
  };
}

export function buildStatusSavePayload(status, context) {
  return buildStatusPayload(status, context);
}

export function hydrateFormFieldsFromStatusApi(fields = [], apiStatuses = []) {
  const list = Array.isArray(fields) ? fields : [];

  return list.map((field) => {
    if (isStatusField(field)) {
      return mergeStatusOptionsFromApi(field, apiStatuses);
    }

    if (isSubStatusField(field)) {
      const parentKey = field?.status_parent_key;
      const parentStatuses = apiStatuses.filter(
        (item) => item?.type_of_status === parentKey
      );
      return hydrateSubStatusFieldFromApi(field, parentStatuses);
    }

    return field;
  });
}
